// Config panel for the "set_multiple_outputs" action block
import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Button
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { qbusApi } from '../../../services/apiClient';

const SetMultipleOutputsConfig = ({ config, locationId, onChange, onSummaryChange }) => {
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(false);

  const entries = config.outputs || [{ outputId: '', value: '' }];

  useEffect(() => {
    if (!locationId) return;
    setLoading(true);
    qbusApi.getOutputsForLocation(locationId)
      .then((res) => setOutputs(res.data.outputs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locationId]);

  const updateEntries = (newEntries) => {
    const updated = { ...config, outputs: newEntries };
    onChange(updated);
    onSummaryChange(`${newEntries.length} output(s)`);
  };

  const handleEntryChange = (index, field, value) => {
    const newEntries = entries.map((e, i) => i === index ? { ...e, [field]: value } : e);
    updateEntries(newEntries);
  };

  const addEntry = () => {
    updateEntries([...entries, { outputId: '', value: '' }]);
  };

  const removeEntry = (index) => {
    if (entries.length <= 1) return;
    updateEntries(entries.filter((_, i) => i !== index));
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Sets multiple Qbus outputs at once, like activating a scene.
      </Typography>

      {entries.map((entry, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControl fullWidth size="small" required>
              <InputLabel>Output</InputLabel>
              <Select
                value={entry.outputId || ''}
                onChange={(e) => handleEntryChange(index, 'outputId', e.target.value)}
                label="Output"
              >
                {outputs.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.naamOmschrijving} ({o.refId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Value"
              value={entry.value ?? ''}
              onChange={(e) => handleEntryChange(index, 'value', e.target.value)}
              required
            />
          </Box>
          <IconButton size="small" onClick={() => removeEntry(index)} disabled={entries.length <= 1}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Paper>
      ))}

      <Button size="small" startIcon={<AddIcon />} onClick={addEntry}>
        Add Output
      </Button>
    </Box>
  );
};

export default SetMultipleOutputsConfig;
